import { Check, ChevronRight, Loader2, Palette } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { usePreferences } from "@/hooks/usePreferences";
import { activeTheme, allThemes, normalizeAppearance, selectTheme } from "@/lib/theme";
import { cn } from "@/lib/utils";

export function ThemeQuickSwitcher({ compact = false, onNavigate }: { compact?: boolean; onNavigate?: () => void }) {
  const { preferences, update } = usePreferences();
  const [open, setOpen] = useState(false);
  const [switchingId, setSwitchingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const appearance = normalizeAppearance(preferences?.appearance);
  const selected = activeTheme(appearance);
  const themes = allThemes(appearance);

  useEffect(() => {
    if (!open) return;
    const closeOnOutside = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", closeOnOutside);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOnOutside);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [open]);

  async function choose(themeId: string) {
    if (!preferences || themeId === selected.id) {
      setOpen(false);
      return;
    }
    setSwitchingId(themeId);
    setError(null);
    try {
      await update({ appearance: selectTheme(appearance, themeId) });
      setOpen(false);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Theme could not be changed.");
    } finally {
      setSwitchingId(null);
    }
  }

  return (
    <div ref={rootRef} className={cn("relative", !compact && "mt-3")}>
      <button
        type="button"
        aria-label={compact ? `Theme: ${selected.name}` : undefined}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
        className={cn(
          "transition-colors focus-visible:outline-none focus-visible:shadow-glow-sm",
          compact
            ? "flex h-11 w-11 items-center justify-center rounded-lg text-studio-muted hover:bg-studio-card hover:text-studio-ink"
            : "flex min-h-11 w-full items-center gap-3 rounded-lg border border-studio-border bg-studio-card px-3 text-left hover:border-studio-border-light"
        )}
      >
        <span className="relative flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-studio-accent-soft text-studio-accent">
          <Palette className="h-3.5 w-3.5" />
          <span className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border-2 border-studio-card" style={{ backgroundColor: selected.colors.accent }} />
        </span>
        {!compact && (
          <>
            <span className="min-w-0 flex-1">
              <span className="block text-[10px] font-medium uppercase tracking-wide text-studio-muted">Theme</span>
              <span className="block truncate text-xs font-semibold text-studio-ink">{selected.name}</span>
            </span>
            <ChevronRight className={cn("h-3.5 w-3.5 text-studio-muted transition-transform", open && "-rotate-90")} />
          </>
        )}
      </button>

      {open && (
        <div
          role="menu"
          aria-label="Choose theme"
          className={cn(
            "absolute z-50 overflow-hidden rounded-xl border border-studio-border bg-studio-panel shadow-panel animate-rise",
            compact ? "right-0 top-full mt-2 w-72" : "bottom-full left-0 mb-2 w-full min-w-56"
          )}
        >
          <div className="border-b border-studio-border px-3 py-2.5">
            <p className="text-xs font-semibold text-studio-ink">Quick theme</p>
            <p className="mt-0.5 text-[10px] text-studio-muted">Switch without leaving your workspace.</p>
          </div>
          <div className="max-h-64 overflow-y-auto p-1.5">
            {themes.map((theme) => {
              const isSelected = theme.id === selected.id;
              const loading = theme.id === switchingId;
              return (
                <button
                  key={theme.id}
                  type="button"
                  role="menuitemradio"
                  aria-checked={isSelected}
                  disabled={switchingId !== null}
                  onClick={() => void choose(theme.id)}
                  className={cn(
                    "flex min-h-10 w-full items-center gap-2 rounded-lg px-2.5 text-left transition-colors disabled:opacity-60",
                    isSelected ? "bg-studio-accent-soft text-studio-accent" : "text-studio-ink hover:bg-studio-card"
                  )}
                >
                  <span className="flex shrink-0 -space-x-1" aria-hidden="true">
                    {[theme.colors.background, theme.colors.surface, theme.colors.accent].map((color, index) => (
                      <span key={`${color}-${index}`} className="h-4 w-4 rounded-full border-2 border-studio-panel" style={{ backgroundColor: color }} />
                    ))}
                  </span>
                  <span className="min-w-0 flex-1 truncate text-xs font-medium">{theme.name}</span>
                  {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : isSelected ? <Check className="h-3.5 w-3.5" /> : null}
                </button>
              );
            })}
          </div>
          {error && <p role="alert" className="border-t border-studio-border px-3 py-2 text-[10px] text-studio-danger">{error}</p>}
          <Link
            to="/settings/appearance"
            onClick={() => { setOpen(false); onNavigate?.(); }}
            className="flex min-h-10 items-center justify-between border-t border-studio-border px-3 text-xs font-semibold text-studio-accent hover:bg-studio-accent-soft"
          >
            Manage themes <ChevronRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      )}
    </div>
  );
}
